import Axios from 'axios'
import moment from 'moment'
import store from '@/store'
moment.locale('de')

// creating new Client so this affects only this file
const axClient = Axios.create()

axClient.interceptors.request.use((config) => {
  config.headers.accessToken = store.state.authentication.backendAccessToken
  config.headers.cubeName = store.state.cube.name
  return config
})

async function scanForNewDevices({ commit, dispatch, state }) {
  try {
    commit('setFormState', {
      form: 'scanForNewDevices',
      state: 'submitting',
    })
    const url = state.urls.deviceNetworkManager + 'scan'
    const result = await axClient.get(url, {
      params: {
        onlyNew: true,
      },
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })
    commit('setFormState', {
      form: 'scanForNewDevices',
      state: 'finished',
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(`Requesting devices returned error: ${result.data.error}`)
    }

    commit('newDevicesReceived', result.data.data.saveResult)
    // Retrieve the current status of all new devices.
    dispatch('getAllDeviceStates')
  } catch (err) {
    dispatch('logError', 'Requesting cube devices failed: ' + err)
  }
}

async function getAllDevices({ commit, dispatch, state }) {
  try {
    const url = state.urls.deviceManager + '/devicesimple'
    const result = await axClient.get(url, {
      params: {
        cubeName: state.cube.name,
      },
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(
        `Requesting devices returned error: ${JSON.stringify(result.data)}`
      )
    }
    commit('devicesReceived', result.data.data)

    // Request the device state for each device we found.
    return await dispatch('getAllDeviceStates')
  } catch (err) {
    dispatch('logError', err)
  }
}

async function getAllDeviceStates({ dispatch, getters }) {
  const promises = []
  const devices = getters.devices
  devices.map((device) => {
    promises.push(dispatch('getDeviceState', device))
  })
  return Promise.all(promises)
}

async function getDeviceState({ dispatch, commit, state }, device) {
  try {
    const url = `${state.urls.deviceControl}report`
    const result = await axClient.get(url, {
      // TODO Change the api so that we only hand over the device id.
      // Querying devices via type and id is error-prone.
      params: {
        type: device.deviceType,
        address: device.ip_address,
      },
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    if (!result || !result.data || !result.data.data || result.data.error) {
      throw new Error(`Error getting device state: ${result.data.data}`)
    }

    commit('deviceStateReceived', {
      deviceId: device.id,
      data: result.data,
    })
  } catch (err) {
    if (
      err.response.status === 422 &&
      err.response.data.data === 'Device not available'
    ) {
      commit('deviceNotReachable', device.id)
    } else {
      dispatch('logError', err)
    }
  }
}

async function setDeviceMode({ commit, dispatch, state }, data) {
  try {
    commit('setFormState', {
      form: 'deviceMode',
      state: 'submitting',
    })

    const url = `${state.urls.deviceManager}/device/${data.deviceId}?mode=${data.mode}`
    const result = await axClient.put(url, {
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    if (!result || !result.data || !result.data.data || result.data.error) {
      throw new Error(`Error setting device mode: ${result.data.error.Message}`)
    }

    commit('setFormState', {
      form: 'deviceMode',
      state: 'finished',
    })

    commit('setDeviceMode', data)
  } catch (err) {
    dispatch('logError', err)
  }
}

async function toggleDeviceOn({ commit, dispatch, state }, device) {
  try {
    commit('setFormState', {
      form: 'deviceOn_' + device.id,
      state: 'submitting',
    })

    // Animate the power icon to show we're waiting for a request to finish.
    commit('setDeviceIsOnChangedLocally', {
      deviceId: device.id,
      value: true,
    })

    const url = `${state.urls.deviceControl}control`
    const result = await axClient.get(url, {
      params: {
        deviceId: device.id,
        type: device.deviceType,
        address: device.ip_address,
        // We need to toggle its state, so invert
        state: !device.isOn ? 'on' : 'off',
      },
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    commit('setFormState', {
      form: 'deviceOn_' + device.id,
      state: 'finished',
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(
        `Error setting device state: ${JSON.stringify(result.data)}`
      )
    }

    // Note that we do not mutate the device's isOn state here because the request
    // we just made can take several seconds to return and in the meantime a
    // sensor data socket event can change isOn back to its previous state until
    // the next event changes it to its correct value.
    // To mitigate this problem we skip setting isOn here and wait until a sensor
    // data socket event tells us to mark the device as being switched on.
    // Instead we set isOnChangedLocally to animate the power icon as pending.
  } catch (err) {
    dispatch('logError', err)
  }
}

async function updateDevice({ commit, dispatch, state }, data) {
  try {
    commit('setFormState', {
      form: 'deviceSettings',
      state: 'submitting',
    })

    let params = {
      id: data.deviceId,
      name: data.name,
      description: data.description,
      appliance_type: data.applianceType,
    }

    const url = `${state.urls.deviceManager}/devicesimple`
    const result = await axClient.put(url, params, {
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    commit('setFormState', {
      form: 'deviceSettings',
      state: 'finished',
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(`Error updating device: ${JSON.stringify(result.data)}`)
    }

    commit('deviceData', {
      deviceId: data.deviceId,
      name: data.name,
      description: data.description,
      appliance_type: data.applianceType,
      deviceType: data.deviceType,
    })
  } catch (err) {
    dispatch('logError', err)
  }
}

async function getDeviceReads({ commit, dispatch, state }, data) {
  try {
    let granularity = '1m'
    let labelsDateFormat = 'HH:mm'
    if (data.timespan !== 'day') {
      granularity = '1d'
      labelsDateFormat = 'dd DD.MM.'
    }

    const today = moment(data.date, 'YYYY-MM-DD')
    let params = {
      deviceId: data.deviceId,
      readingTypes: 'Consumption W',
      labelsDateFormat,
      granularity,
      startDate: today.startOf(data.timespan).toISOString(),
      endDate: today.endOf(data.timespan).toISOString(),
    }

    const url = `${state.urls.deviceManager}/app/device-reads`
    const result = await axClient.get(url, {
      params: params,
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(`Error updating device: ${JSON.stringify(result.data)}`)
    }

    commit('deviceReads', {
      deviceId: parseInt(data.deviceId),
      result: result.data.data,
      readingType: 'Consumption W',
      timespan: data.timespan,
    })
  } catch (err) {
    dispatch('logError', err)
  }
}

async function saveDeviceTimer({ commit, dispatch, getters, state }, deviceId) {
  try {
    commit('setFormState', {
      form: 'deviceTimer',
      state: 'submitting',
    })
    const device = getters.deviceById(deviceId)

    let params = {
      id: deviceId,
      ...getters.deviceTimerSettingsForBackend(deviceId),
      mode: device.timer.active ? 'manual' : 'measure',
      default_state: device.timer.defaultOnState,
    }

    const url = `${state.urls.deviceManager}/devicesimple`
    const result = await axClient.put(url, params, {
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    commit('deviceTimerSaved', deviceId)

    commit('setFormState', {
      form: 'deviceTimer',
      state: 'finished',
    })

    if (!result || !result.data || result.data.error) {
      throw new Error(
        `Error updating device timer: ${JSON.stringify(result.data)}`
      )
    }
  } catch (err) {
    dispatch('logError', err)
  }
}

async function deleteDevice({ commit, state, dispatch }, deviceId) {
  try {
    commit('setFormState', {
      form: 'deleteDevice',
      state: 'submitting',
    })

    const url = `${state.urls.deviceManager}/device/${deviceId}`
    const result = await axClient.delete(url, {
      headers: {
        accessToken: state.authentication.backendAccessToken,
        cubeName: state.cube.name,
      },
    })

    commit('setFormState', {
      form: 'deleteDevice',
      state: 'finished',
    })

    commit('deviceDeleted', deviceId)

    if (!result || !result.data || result.data.error) {
      throw new Error(`Error deleting device : ${JSON.stringify(result.data)}`)
    }
  } catch (err) {
    dispatch('logError', err)
  }
}

export default {
  getAllDevices,
  scanForNewDevices,
  setDeviceMode,
  toggleDeviceOn,
  getDeviceState,
  getAllDeviceStates,
  updateDevice,
  getDeviceReads,
  saveDeviceTimer,
  deleteDevice,
}
